import { useState } from "react";
import { toast } from "sonner";
import { Zap, Mail, Clock, GitBranch, CheckSquare, Plus, X, ArrowUp, ArrowDown, Play, Pause, Rocket, Users } from "lucide-react";
import { api } from "../../lib/api";

const stepIcon = (t) => {
  const s = (t || "").toLowerCase();
  if (s.includes("correo") || s.includes("email") || s.includes("envi")) return Mail;
  if (s.includes("esper") || s.includes("día") || s.includes("dia") || s.includes("hora") || s.includes("wait")) return Clock;
  if (s.includes("si ") || s.includes("condi") || s.includes("verific")) return GitBranch;
  if (s.includes("tarea") || s.includes("asign") || s.includes("llam")) return CheckSquare;
  return Zap;
};

function Node({ icon: Icon, tone, title, sub, children }) {
  return (
    <div className={`relative w-full max-w-md rounded-xl border p-3.5 flex items-start gap-3 ${tone}`}>
      <div className="w-9 h-9 rounded-lg bg-white/70 flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug break-words">{title}</div>
        {sub && <div className="text-[11px] opacity-60 mt-0.5">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const Connector = () => <div className="w-px h-5 bg-navy/15 my-1" />;

export default function ModuleAutomation({ moduleKey, item, onRefresh }) {
  const parse = (v) => String(v || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const [steps, setSteps] = useState(parse(item.pasos));
  const [nuevo, setNuevo] = useState("");
  const [saving, setSaving] = useState(false);
  const activo = item.estado === "activo";

  const persist = async (arr, patch = {}) => {
    setSaving(true);
    try {
      await api.patch(`/mod/${moduleKey}/items/${item.id}`, { pasos: arr.join("\n"), ...patch });
      setSteps(arr);
      onRefresh?.();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const add = () => { if (!nuevo.trim()) return; const arr = [...steps, nuevo.trim()]; setNuevo(""); persist(arr); };
  const remove = (i) => persist(steps.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= steps.length) return;
    const arr = [...steps]; [arr[i], arr[j]] = [arr[j], arr[i]]; persist(arr);
  };
  const toggleEstado = () => persist(steps, { estado: activo ? "pausado" : "activo" });
  const [running, setRunning] = useState(false);
  const run = async () => {
    if (!steps.length) return toast.error("Agrega al menos un paso antes de ejecutar");
    setRunning(true);
    try {
      const { data } = await api.post(`/mod/${moduleKey}/items/${item.id}/run`);
      toast.success(`Flujo ejecutado: ${data.enrolled} lead(s) inscrito(s)`);
      onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo ejecutar"); }
    finally { setRunning(false); }
  };
  const ejecuciones = item.ejecuciones || [];

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-3" data-testid="module-automation">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 overline text-navy/40"><Zap className="w-3.5 h-3.5" /> Flujo de automatización</div>
        <button onClick={toggleEstado} disabled={saving} data-testid="automation-toggle"
          className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5 transition-colors ${activo ? "bg-emerald-500/10 text-emerald-600" : "bg-navy/5 text-navy/50"}`}>
          {activo ? <><Play className="w-3 h-3" /> Activo</> : <><Pause className="w-3 h-3" /> Pausado</>}
        </button>
      </div>

      <div className="flex flex-col items-center py-2">
        <Node icon={Zap} tone="bg-violet/5 border-violet/30 text-violet"
          title={item.disparador || "Disparador"} sub="Cuando se cumple, inicia el flujo" />
        {steps.length > 0 && <Connector />}

        {steps.map((s, i) => {
          const Icon = stepIcon(s);
          return (
            <div key={i} className="w-full flex flex-col items-center" data-testid={`automation-step-${i}`}>
              <Node icon={Icon} tone="bg-white border-navy/10 text-navy/80" title={s} sub={`Paso ${i + 1}`}>
                <div className="flex flex-col gap-0.5 text-navy/30">
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="hover:text-navy/60 disabled:opacity-20"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="hover:text-navy/60 disabled:opacity-20"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(i)} className="hover:text-destructive" data-testid={`automation-remove-${i}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              </Node>
              <Connector />
            </div>
          );
        })}

        <div className="w-full max-w-md flex gap-2">
          <input value={nuevo} onChange={(e) => setNuevo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
            placeholder="Añadir paso (ej. Enviar correo de bienvenida, Esperar 2 días…)" data-testid="automation-new-step"
            className="flex-1 bg-white border border-dashed border-navy/20 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm" />
          <button onClick={add} className="btn btn-primary" data-testid="automation-add-btn"><Plus className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="border-t border-navy/10 pt-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-navy/50 flex items-center gap-3">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {item.inscritos || 0} inscritos</span>
          <span>{ejecuciones.length} ejecución(es)</span>
        </div>
        <button onClick={run} disabled={running} className="btn btn-primary" data-testid="automation-run-btn">
          {running ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Rocket className="w-4 h-4" />} Ejecutar ahora
        </button>
      </div>
    </div>
  );
}
